import React from 'react';
import { observer } from 'mobx-react-lite';
import { CustomDropdown, FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';
import { SourceOfFundsState } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.state';
import { SourceOfFundsActionPicker } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsActionPicker';
import {
    SourceOfFundsAmountInput,
    SourceOfFundsDescription,
    SourceOfFundsFieldCell,
    SourceOfFundsFieldError,
    SourceOfFundsFieldRow,
    SourceOfFundsSection,
    SourceOfFundsSectionTitle,
    SourceOfFundsSelectorWrapper,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.style';
import {
    SourceOfFundsCalculationType,
    SourceOfFundsCalendarPeriodValueType,
    SourceOfFundsPeriodType,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.types';
import {
    SOURCE_OF_FUNDS_CALCULATION_TYPE_OPTIONS_ARRAY,
    SOURCE_OF_FUNDS_CALENDAR_PERIOD_OPTIONS_ARRAY,
    SOURCE_OF_FUNDS_PERIOD_TYPE_OPTIONS_ARRAY,
    isSourceOfFundsCalculationType,
    isSourceOfFundsCalendarPeriodValue,
    isSourceOfFundsPeriodType,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsThresholdOptions';

interface SourceOfFundsThresholdPanelPropsType {
    state: SourceOfFundsState;
}

export const SourceOfFundsThresholdPanel = observer(({ state }: SourceOfFundsThresholdPanelPropsType): JSX.Element => {
    return (
        <SourceOfFundsSection data-test='source-of-funds-threshold-panel'>
            <SourceOfFundsSectionTitle>Standard Deposit Threshold</SourceOfFundsSectionTitle>
            <SourceOfFundsDescription>
                Sets the cumulative net or gross deposit limit and trailing calendar month window or rolling day window.
                Exceeding this limit - or initiating a manual SoF trigger from an operator - automatically updates the
                account&apos;s SoF status to &quot;requested&quot; and executes the selected actions (compliance
                restriction, email or pop-up).
            </SourceOfFundsDescription>

            <SourceOfFundsFieldRow>
                <SourceOfFundsFieldCell>
                    <SourceOfFundsAmountInput
                        $hasError={state.thresholdAmountError !== null}
                        data-test='source-of-funds-threshold-amount'
                        inputMode='numeric'
                        onChange={(event): void => {
                            state.setThresholdAmount(event.target.value);
                        }}
                        type='text'
                        value={state.thresholdAmount}
                    />
                    <SourceOfFundsFieldError data-test='source-of-funds-threshold-amount-error'>
                        {state.thresholdAmountError ?? ''}
                    </SourceOfFundsFieldError>
                </SourceOfFundsFieldCell>
                <SourceOfFundsFieldCell>
                    <span>£</span>
                    <SourceOfFundsFieldError />
                </SourceOfFundsFieldCell>
                <SourceOfFundsFieldCell>
                    <SourceOfFundsSelectorWrapper>
                        <CustomDropdown
                            dataTest='source-of-funds-threshold-calculation-type'
                            hasInternalLabel={false}
                            onChangeCallback={(value): void => {
                                if (typeof value === 'string' && isSourceOfFundsCalculationType(value)) {
                                    state.setThresholdCalculationType(value);
                                }
                            }}
                            optionsList={(): Array<FilterOptionType<SourceOfFundsCalculationType>> =>
                                SOURCE_OF_FUNDS_CALCULATION_TYPE_OPTIONS_ARRAY
                            }
                            placeholder='Net/Gross'
                            selectedValue={(): SourceOfFundsCalculationType => state.thresholdCalculationType}
                            useMenuPortal
                        />
                    </SourceOfFundsSelectorWrapper>
                    <SourceOfFundsFieldError />
                </SourceOfFundsFieldCell>
                <SourceOfFundsFieldCell>
                    <span>deposits on a</span>
                    <SourceOfFundsFieldError />
                </SourceOfFundsFieldCell>
                <SourceOfFundsFieldCell>
                    <SourceOfFundsSelectorWrapper>
                        <CustomDropdown
                            dataTest='source-of-funds-threshold-period-type'
                            hasInternalLabel={false}
                            onChangeCallback={(value): void => {
                                if (typeof value === 'string' && isSourceOfFundsPeriodType(value)) {
                                    state.setThresholdPeriodType(value);
                                }
                            }}
                            optionsList={(): Array<FilterOptionType<SourceOfFundsPeriodType>> =>
                                SOURCE_OF_FUNDS_PERIOD_TYPE_OPTIONS_ARRAY
                            }
                            placeholder='Rolling/Calendar'
                            selectedValue={(): SourceOfFundsPeriodType => state.thresholdPeriodType}
                            useMenuPortal
                        />
                    </SourceOfFundsSelectorWrapper>
                    <SourceOfFundsFieldError />
                </SourceOfFundsFieldCell>
                {state.thresholdPeriodType === 'rolling' ? (
                    <>
                        <SourceOfFundsFieldCell>
                            <SourceOfFundsAmountInput
                                $hasError={state.thresholdRollingPeriodDaysError !== null}
                                data-test='source-of-funds-threshold-rolling-period'
                                inputMode='numeric'
                                onChange={(event): void => {
                                    state.setThresholdRollingPeriodDays(event.target.value);
                                }}
                                placeholder='Number of days'
                                type='text'
                                value={state.thresholdRollingPeriodDays}
                            />
                            <SourceOfFundsFieldError data-test='source-of-funds-threshold-rolling-period-error'>
                                {state.thresholdRollingPeriodDaysError ?? ''}
                            </SourceOfFundsFieldError>
                        </SourceOfFundsFieldCell>
                        <SourceOfFundsFieldCell>
                            <span>days</span>
                            <SourceOfFundsFieldError />
                        </SourceOfFundsFieldCell>
                    </>
                ) : (
                    <SourceOfFundsFieldCell>
                        <SourceOfFundsSelectorWrapper>
                            <CustomDropdown
                                dataTest='source-of-funds-threshold-calendar-period'
                                hasInternalLabel={false}
                                onChangeCallback={(value): void => {
                                    if (typeof value === 'string' && isSourceOfFundsCalendarPeriodValue(value)) {
                                        state.setThresholdCalendarPeriodValue(value);
                                    }
                                }}
                                optionsList={(): Array<FilterOptionType<SourceOfFundsCalendarPeriodValueType>> =>
                                    SOURCE_OF_FUNDS_CALENDAR_PERIOD_OPTIONS_ARRAY
                                }
                                placeholder='Select period'
                                selectedValue={(): SourceOfFundsCalendarPeriodValueType =>
                                    state.thresholdCalendarPeriodValue
                                }
                                useMenuPortal
                            />
                        </SourceOfFundsSelectorWrapper>
                        <SourceOfFundsFieldError />
                    </SourceOfFundsFieldCell>
                )}
            </SourceOfFundsFieldRow>

            <SourceOfFundsActionPicker
                complianceRestrictionPeriod={state.thresholdComplianceRestrictionPeriod}
                dataTestPrefix='source-of-funds-threshold'
                onChangeComplianceRestrictionPeriod={state.setThresholdComplianceRestrictionPeriod}
                onClearAll={state.clearThresholdActions}
                onSetAction={state.setThresholdAction}
                selection={state.thresholdActions}
            />
        </SourceOfFundsSection>
    );
});
